/**
 * Hook pour déterminer le type de vendeur d'une société
 * et en déduire si l'utilisateur peut choisir le type de garantie
 */

import { useMemo } from 'react';
import { useSociety } from './useSocieties';
import { VENDOR_TYPES } from '../types/society.types';

interface UseSocietyVendorTypeResult {
  vendorType: number | undefined;
  canChooseWarrantyType: boolean;
  autoWarrantyType: 0 | 1 | undefined;
  isLoading: boolean;
  vendorTypeLabel: string | undefined;
}

/**
 * Détermine si l'utilisateur peut choisir le type de garantie
 * selon le vendorType de la société
 *
 * Règles :
 * - GARANT (1) → warrantyType = 0 (Garantie) - pas de choix
 * - PRESTATAIRE (2) → warrantyType = 1 (Prestation) - pas de choix
 * - BOTH (3) → L'utilisateur peut choisir - afficher le sélecteur
 */
export const useSocietyVendorType = (societyId: string | undefined): UseSocietyVendorTypeResult => {
  const { data: society, isLoading } = useSociety(societyId);

  const result = useMemo<UseSocietyVendorTypeResult>(() => {
    if (!society) {
      return {
        vendorType: undefined,
        canChooseWarrantyType: false,
        autoWarrantyType: undefined,
        isLoading,
        vendorTypeLabel: undefined,
      };
    }

    const vendorType = society.vendorType;

    if (vendorType === VENDOR_TYPES.BOTH) {
      return {
        vendorType,
        canChooseWarrantyType: true,
        autoWarrantyType: undefined,
        isLoading,
        vendorTypeLabel: 'Garant et Prestataire',
      };
    }

    if (vendorType === VENDOR_TYPES.GARANT) {
      return {
        vendorType,
        canChooseWarrantyType: false,
        autoWarrantyType: 0,
        isLoading,
        vendorTypeLabel: 'Garant',
      };
    }

    if (vendorType === VENDOR_TYPES.PRESTATAIRE) {
      return {
        vendorType,
        canChooseWarrantyType: false,
        autoWarrantyType: 1,
        isLoading,
        vendorTypeLabel: 'Prestataire',
      };
    }

    return {
      vendorType,
      canChooseWarrantyType: false,
      autoWarrantyType: undefined,
      isLoading,
      vendorTypeLabel: undefined,
    };
  }, [society, isLoading]);

  return result;
};
